import React, { useEffect, useState, useCallback } from 'react';
import { ReactFlow, MiniMap, Controls, Background } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button, Tooltip, Spin } from 'antd';
import { DeleteOutlined, CloseOutlined, BulbOutlined } from '@ant-design/icons';

// 定義空的 edgeTypes 對象，避免每次渲染重新創建
const emptyEdgeTypes = {};

// 多選提示記憶 key（一次關閉後不再顯示）
const MULTI_SELECT_HINT_DISMISSED_KEY = 'wfDesigner.multiSelectHintDismissed';

// 依平台顯示對應的修飾鍵名稱（Mac 顯示 ⌘，其他平台顯示 Ctrl）
const isMacPlatform = () => {
  if (typeof navigator === 'undefined') return false;
  const src = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
  return /Mac|iPod|iPhone|iPad/i.test(src);
};

// React Flow 畫布組件
const WorkflowCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onDrop,
  onDragOver,
  onNodeClick,
  onNodeDoubleClick,
  onEdgeClick,
  onEdgeMouseEnter,
  onEdgeMouseLeave,
  nodeTypes,
  nodeTypesComponents,
  edgeTypes,
  onEdgeSwitch,
  onMove,
  handleInit,
  selectedEdge,
  hoveredEdge,
  nodes: allNodes,
  onEdgeDelete,
  reactFlowInstanceRef,
  onNodeSelect,
  onCanvasClick,
  onKeyDown,
  onSelectionChange,
  t,
  isWorkflowLoading = false,
}) => {
  // React Flow 官方互動屬性：載入時關閉拖曳/連線/選取，避免用戶誤點中斷載入
  const nodesDraggable = !isWorkflowLoading;
  const nodesConnectable = !isWorkflowLoading;
  const elementsSelectable = !isWorkflowLoading;

  // 多選提示卡片：第一次進入畫面顯示，按「知道了」之後存入 localStorage 不再顯示
  const [showMultiSelectHint, setShowMultiSelectHint] = useState(() => {
    try {
      return localStorage.getItem(MULTI_SELECT_HINT_DISMISSED_KEY) !== '1';
    } catch (e) {
      return true;
    }
  });
  const dismissMultiSelectHint = useCallback(() => {
    try {
      localStorage.setItem(MULTI_SELECT_HINT_DISMISSED_KEY, '1');
    } catch (e) {
      // 忽略 localStorage 寫入失敗（例如隱私模式）
    }
    setShowMultiSelectHint(false);
  }, []);
  const modKeyLabel = isMacPlatform() ? '⌘' : 'Ctrl';

  // 添加鍵盤事件監聽
  useEffect(() => {
    const handleKeyDown = (event) => {
      onKeyDown(event);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onKeyDown]);


  // 包裝節點點擊處理
  const handleNodeClick = (event, node) => {
    console.log('WorkflowCanvas handleNodeClick called:', { 
      nodeId: node.id, 
      hasOnNodeSelect: !!onNodeSelect,
      hasOnNodeClick: !!onNodeClick,
      eventType: event.type,
      ctrlKey: event.ctrlKey || event.metaKey
    });
    
    // 調用 onNodeClick 來處理其他邏輯（如設置 selectedNode）
    if (onNodeClick) {
      onNodeClick(event, node);
    }
    
    // 調用 onNodeSelect 來記錄事件（但不處理選擇邏輯）
    if (onNodeSelect) {
      onNodeSelect(event, node);
    }
  };

  // 包裝節點雙擊處理
  const handleNodeDoubleClick = (event, node) => {
    console.log('WorkflowCanvas handleNodeDoubleClick called:', { 
      nodeId: node.id, 
      hasOnNodeDoubleClick: !!onNodeDoubleClick,
      eventType: event.type
    });
    
    if (onNodeDoubleClick) {
      onNodeDoubleClick(event, node);
    }
  };

  // 處理畫布右鍵點擊 - 觸發 fit view
  const handlePaneContextMenu = (event) => {
    event.preventDefault();
    if (reactFlowInstanceRef?.current) {
      reactFlowInstanceRef.current.fitView({ padding: 0.1 });
    }
  };


  return (
    <div style={{ flex: 1, position: 'relative' }}>
      <ReactFlow
        onInit={handleInit}
        nodes={nodes}
        edges={edges}
        nodesDraggable={nodesDraggable}
        nodesConnectable={nodesConnectable}
        elementsSelectable={elementsSelectable}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectStart={(event, { nodeId, handleId, handleType }) => {
          console.log('🚀 onConnectStart:', { nodeId, handleId, handleType });
        }}
        onConnectEnd={(event, { nodeId, handleId, handleType }) => {
          console.log('🏁 onConnectEnd:', { nodeId, handleId, handleType });
        }}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onPaneClick={onCanvasClick}
        onPaneContextMenu={handlePaneContextMenu}
        onEdgeClick={onEdgeClick}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        nodeTypes={nodeTypesComponents}
        edgeTypes={edgeTypes || emptyEdgeTypes}
        style={{ height: '100%', width: '100%' }}
        onMove={onMove}
        selectionOnDrag={false}
        panOnDrag={[0]}
        selectionMode="partial"
        selectionKeyCode="Shift"
        multiSelectionKeyCode="Control"
        onSelectionChange={onSelectionChange}
        panOnScroll={false}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomActivationKeyCode={null}
        zoomOnDoubleClick={false}
        onDoubleClick={(e) => {
          // 明確阻止雙擊縮放
          e.preventDefault();
          e.stopPropagation();
        }}
        preventScrolling={false}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: true,
          style: { 
            strokeWidth: 3,
            stroke: '#1890ff'
          },
          markerEnd: {
            type: 'arrowclosed',
            width: 12,
            height: 12,
            color: '#1890ff',
          },
          data: { onEdgeSwitch },
        }}
      >
        <MiniMap />
        <Controls />
        <Background />

        {/* 多選操作提示（第一次進入畫面顯示，可關閉並記住偏好） */}
        {showMultiSelectHint && !isWorkflowLoading && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 50,
              maxWidth: 320,
              padding: '10px 12px',
              borderRadius: 8,
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              pointerEvents: 'auto',
            }}
            role="note"
            aria-live="polite"
          >
            <BulbOutlined style={{ color: '#faad14', fontSize: 16, marginTop: 2, flexShrink: 0 }} />
            <div style={{ flex: 1, fontSize: 12, lineHeight: 1.6, color: '#613400' }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                {t('workflowDesigner.multiSelectHintTitle')}
              </div>
              <div>
                {t('workflowDesigner.multiSelectHint', { key: modKeyLabel })
                  .split(modKeyLabel)
                  .map((part, idx, arr) => (
                    <React.Fragment key={idx}>
                      {part}
                      {idx < arr.length - 1 && (
                        <kbd
                          style={{
                            display: 'inline-block',
                            padding: '0 6px',
                            margin: '0 2px',
                            borderRadius: 4,
                            border: '1px solid #d9b884',
                            background: '#fff',
                            color: '#613400',
                            fontFamily: 'inherit',
                            fontWeight: 600,
                            fontSize: 11,
                            lineHeight: '18px',
                            verticalAlign: 'baseline',
                          }}
                        >
                          {modKeyLabel}
                        </kbd>
                      )}
                    </React.Fragment>
                  ))}
              </div>
              <div style={{ marginTop: 6, textAlign: 'right' }}>
                <Button type="link" size="small" onClick={dismissMultiSelectHint} style={{ padding: 0, height: 'auto', fontSize: 12 }}>
                  {t('workflowDesigner.gotIt')}
                </Button>
              </div>
            </div>
            <Tooltip title={t('workflowDesigner.gotIt')} placement="left">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined style={{ fontSize: 12 }} />}
                onClick={dismissMultiSelectHint}
                aria-label={t('workflowDesigner.gotIt')}
                style={{
                  width: 22,
                  height: 22,
                  minWidth: 22,
                  padding: 0,
                  flexShrink: 0,
                  color: '#8c6e3c',
                }}
              />
            </Tooltip>
          </div>
        )}

        {/* 選中連接線的刪除按鈕 */}
        {selectedEdge && (() => {
          const sourceNode = allNodes.find(n => n.id === selectedEdge.source);
          const targetNode = allNodes.find(n => n.id === selectedEdge.target);
          if (!sourceNode || !targetNode) return null;
          
          // 獲取 React Flow 實例
          const reactFlowInstance = reactFlowInstanceRef?.current;
          if (!reactFlowInstance) return null;
          
          // 獲取視圖的變換信息
          const viewport = reactFlowInstance.getViewport();
          
          // 計算節點在視圖中的實際位置（考慮節點大小）
          const sourceX = (sourceNode.position.x * viewport.zoom) + viewport.x + (120 * viewport.zoom / 2); // 節點寬度的一半
          const sourceY = (sourceNode.position.y * viewport.zoom) + viewport.y + (40 * viewport.zoom / 2); // 節點高度的一半
          const targetX = (targetNode.position.x * viewport.zoom) + viewport.x + (120 * viewport.zoom / 2);
          const targetY = (targetNode.position.y * viewport.zoom) + viewport.y + (40 * viewport.zoom / 2);
          
          // 計算連接線中點
          const centerX = (sourceX + targetX) / 2;
          const centerY = (sourceY + targetY) / 2;
          
          return (
            <div
              style={{
                position: 'absolute',
                left: centerX - 30,
                top: centerY - 15,
                zIndex: 1000,
                pointerEvents: 'auto',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <Tooltip title={t('workflowDesigner.delete')} placement="top">
                <Button
                  danger
                  size="small"
                  onClick={() => onEdgeDelete(selectedEdge.id)}
                  icon={<DeleteOutlined />}
                  style={{
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    border: '2px solid #fff',
                    backgroundColor: '#ff4d4f',
                    color: '#fff',
                    fontWeight: 'bold',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0'
                  }}
                />
              </Tooltip>
            </div>
          );
        })()}
        
        {/* 懸停提示 */}
        {hoveredEdge && !selectedEdge && (() => {
          const sourceNode = allNodes.find(n => n.id === hoveredEdge.source);
          const targetNode = allNodes.find(n => n.id === hoveredEdge.target);
          if (!sourceNode || !targetNode) return null;
          
          // 獲取 React Flow 實例
          const reactFlowInstance = reactFlowInstanceRef?.current;
          if (!reactFlowInstance) return null;
          
          // 獲取視圖的變換信息
          const viewport = reactFlowInstance.getViewport();
          
          // 計算節點在視圖中的實際位置（考慮節點大小）
          const sourceX = (sourceNode.position.x * viewport.zoom) + viewport.x + (120 * viewport.zoom / 2);
          const sourceY = (sourceNode.position.y * viewport.zoom) + viewport.y + (40 * viewport.zoom / 2);
          const targetX = (targetNode.position.x * viewport.zoom) + viewport.x + (120 * viewport.zoom / 2);
          const targetY = (targetNode.position.y * viewport.zoom) + viewport.y + (40 * viewport.zoom / 2);
          
          // 計算連接線中點
          const centerX = (sourceX + targetX) / 2;
          const centerY = (sourceY + targetY) / 2;
          
          return (
            <div
              style={{
                position: 'absolute',
                left: centerX - 40,
                top: centerY - 20,
                zIndex: 999,
                pointerEvents: 'none',
                transform: 'translate(-50%, -50%)'
              }}
            >
              <div
                style={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  color: '#fff',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
              >
                {t('workflowDesigner.clickToSelectConnection')}
              </div>
            </div>
          );
        })()}
      </ReactFlow>
      
      {/* 載入中遮罩：凍結 UI、阻擋誤點，流程圖仍正常渲染於底層 */}
      {isWorkflowLoading && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            pointerEvents: 'auto',
          }}
          aria-busy="true"
          aria-live="polite"
        >
          <Spin size="large" tip={t?.('workflowDesigner.loadingWorkflow') ?? '載入流程中…'} />
        </div>
      )}
    </div>
  );
};

export default WorkflowCanvas;
